import { Alert, Anchor, Loader, Stack, Text } from '@mantine/core';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Set up PDF.js worker - using legacy build
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  file: string;
  width?: number;
}

export const PDFViewer = ({ file, width = 800 }: PDFViewerProps) => {
  const { t } = useTranslation();
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        const loadingTask = pdfjs.getDocument(file);
        const pdfDocument = await loadingTask.promise;

        setPdf(pdfDocument);

        // Initialize canvas refs array
        canvasRefs.current = new Array(pdfDocument.numPages).fill(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
      } finally {
        setLoading(false);
      }
    };

    if (file) {
      loadPDF();
    }
  }, [file]);

  useEffect(() => {
    if (!pdf || !canvasRefs.current.length) return;

    let cancelled = false;
    const renderTasks: pdfjs.RenderTask[] = [];

    const renderPages = async () => {
      try {
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) break;

          const canvas = canvasRefs.current[pageNum - 1];
          if (!canvas) continue;

          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1 });

          // Calculate scale to fit width
          const scale = Math.min(width / viewport.width, 2); // Max scale of 2
          const scaledViewport = page.getViewport({ scale });

          // Clear canvas and set dimensions
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;
          canvas.style.width = `${scaledViewport.width}px`;
          canvas.style.height = `${scaledViewport.height}px`;

          const context = canvas.getContext('2d');
          if (!context) continue;

          // Clear any previous content
          context.clearRect(0, 0, canvas.width, canvas.height);

          if (cancelled) break;

          const renderTask = page.render({
            canvasContext: context,
            viewport: scaledViewport,
            canvas: canvas,
          });

          renderTasks.push(renderTask);

          try {
            await renderTask.promise;
          } catch (err) {
            if (!cancelled) {
              console.error(`Error rendering page ${pageNum}:`, err);
              setError('Error rendering PDF pages');
            }
            break;
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error in renderPages:', err);
          setError('Error rendering PDF pages');
        }
      }
    };

    renderPages();

    // Cleanup function
    return () => {
      cancelled = true;
      renderTasks.forEach((task) => {
        if (task && task.cancel) {
          task.cancel();
        }
      });
    };
  }, [pdf, width]);

  if (loading) {
    return (
      <Stack align="center" gap="md" p="md">
        <Loader />
        <Anchor
          fw="bold"
          href={file}
          rel="noopener noreferrer"
          size="xl"
          ta="center"
          target="_blank"
          td="underline"
        >
          {t('pdf-viewer.open-new-tab')}
        </Anchor>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack align="center" gap="md">
        <Alert color="red" title="Error loading PDF">
          {error}
        </Alert>
        <Anchor
          fw="bold"
          href={file}
          rel="noopener noreferrer"
          size="xl"
          ta="center"
          target="_blank"
          td="underline"
        >
          {t('pdf-viewer.open-new-tab')}
        </Anchor>
      </Stack>
    );
  }

  if (!pdf) {
    return null;
  }

  return (
    <Stack align="center" gap="md">
      {Array.from({ length: pdf.numPages }, (_, index) => (
        <canvas
          key={`page_${index + 1}`}
          ref={(el) => {
            canvasRefs.current[index] = el;
          }}
          style={{
            maxWidth: '100%',
            height: 'auto',
          }}
        />
      ))}
    </Stack>
  );
};
